import { animate } from "motion";
import { ACCESSORIES, COURT_TYPES, getCourtRules } from "./config";
import "./court-builder.css";

const FORM_ID = "CourtBuilderForm_CourtBuilderForm";

export function initCourtBuilder(root = document) {
  const shell = root instanceof Element && root.id === "mfcbroot" ? root : root.querySelector("#mfcbroot");
  if (!shell || shell.dataset.courtBuilderInitialized === "true") {
    return;
  }

  shell.dataset.courtBuilderInitialized = "true";

  const abortController = new AbortController();
  const { signal } = abortController;
  const colorMapEl = document.getElementById("court-builder-colors");
  const colorMap = colorMapEl ? JSON.parse(colorMapEl.textContent) : {};
  const LINE_COLORS = { White: "#fff", Black: "#000" };
  const IS_EMBED = shell.dataset.courtBuilderEmbedded === "true";
  const SVG_URL = shell.dataset.courtBuilderSvgUrl || "";
  const SVG_BASE = withTrailingSlash(shell.dataset.courtBuilderSvgBase || "/_resources/themes/app/dist/court-builder/");
  const printLink = shell.querySelector("[data-court-builder-print-fragment]");
  const resetLink = shell.querySelector("[data-court-builder-reset]");

  printLink?.addEventListener(
    "click",
    (event) => {
      if (!IS_EMBED || !window.MateflexCourtBuilderEmbed?.print) {
        return;
      }

      event.preventDefault();
      window.MateflexCourtBuilderEmbed.print(printLink.dataset.courtBuilderPrintFragment).catch((error) => {
        console.error("[court-builder] print", error);
        window.open(printLink.href, "_blank", "noopener");
      });
    },
    { signal },
  );

  resetLink?.addEventListener(
    "click",
    (event) => {
      if (!IS_EMBED || typeof window._mfhcembedNavigate !== "function") {
        return;
      }

      event.preventDefault();
      window._mfhcembedNavigate(resetLink.href);
    },
    { signal },
  );

  const form = shell.querySelector(".court-builder-options-form");
  if (!form) {
    shell._courtBuilderCleanup = () => {
      abortController.abort();
    };
    return;
  }

  const svgShell = shell.querySelector("#SVGShell");
  const welcomePanel = shell.querySelector("#Welcome");
  const specsButton = shell.querySelector("#SpecsButton");
  const specsButtonLabel = specsButton?.querySelector("[data-specs-button-label]");
  const sidebarSubmitButton = shell.querySelector("#sidebar-submit-button");
  const backdrop = shell.querySelector("#CBBackdrop");
  const courtTypeHolder = shell.querySelector(`#${FORM_ID}_CourtType_Holder`);
  const keyColorHolder = shell.querySelector(`#${FORM_ID}_KeyColor_Holder`);
  const courtTypeTrigger = courtTypeHolder?.querySelector("[data-court-type-trigger]");
  const courtTypeValue = courtTypeHolder?.querySelector("[data-court-type-value]");
  const courtTypeMenu = courtTypeHolder?.querySelector(".middleColumn");
  const sectionLabels = form.querySelectorAll(`.field:not(#${FORM_ID}_CourtType_Holder) > [data-section-label]`);
  const selectedValueLabels = {
    CourtColor: shell.querySelector(`#${FORM_ID}_CourtColor_Holder [data-section-selected-value]`),
    BorderColor: shell.querySelector(`#${FORM_ID}_BorderColor_Holder [data-section-selected-value]`),
    KeyColor: shell.querySelector(`#${FORM_ID}_KeyColor_Holder [data-section-selected-value]`),
    LineColor: shell.querySelector(`#${FORM_ID}_LineColor_Holder [data-section-selected-value]`),
  };
  let colorTooltipInstances = [];

  initColorTooltips().then((instances) => {
    if (signal.aborted) {
      destroyTooltips(instances);
      return;
    }

    colorTooltipInstances = instances;
  });

  const sizeHolders = {};
  for (const t of COURT_TYPES) {
    sizeHolders[t] = shell.querySelector(`#${FORM_ID}_Sizes_${t}_Holder`);
  }

  let currentCombo = "";
  let currentCourtType = "";
  const sectionAnimations = new WeakMap();
  const DEFAULT_SELECTIONS = {
    CourtColor: getRadioValue("CourtColor"),
    BorderColor: getRadioValue("BorderColor"),
    KeyColor: getRadioValue("KeyColor"),
    LineColor: getRadioValue("LineColor"),
  };

  function getRadioValue(name) {
    const el = form.querySelector(`input[name="${name}"]:checked`);
    return el ? el.value : "";
  }

  function clearRadioGroup(name) {
    for (const input of form.querySelectorAll(`input[name="${name}"]`)) {
      input.checked = false;
    }
  }

  function setRadioValue(name, value) {
    if (!value) {
      clearRadioGroup(name);
      return;
    }

    const input = form.querySelector(`input[name="${name}"][value="${CSS.escape(value)}"]`);
    if (input) {
      input.checked = true;
    }
  }

  function resetDependentFields(nextCourtType) {
    for (const courtType of COURT_TYPES) {
      clearRadioGroup(`Sizes_${courtType}`);
    }

    setRadioValue("CourtColor", DEFAULT_SELECTIONS.CourtColor);
    setRadioValue("BorderColor", DEFAULT_SELECTIONS.BorderColor);
    setRadioValue("KeyColor", DEFAULT_SELECTIONS.KeyColor);
    setRadioValue("LineColor", DEFAULT_SELECTIONS.LineColor);

    for (const accessory of ACCESSORIES) {
      const input = form.querySelector(`input[name="${accessory.field}"]`);
      if (input) {
        input.checked = false;
      }
    }

    if (!nextCourtType || nextCourtType === "Choose") {
      return;
    }

    clearRadioGroup(`Sizes_${nextCourtType}`);
  }

  function getSelectedLabel(name) {
    const input = form.querySelector(`input[name="${name}"]:checked`);
    if (!input) return "";

    const label = form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    return label ? label.textContent.trim() : input.value;
  }

  function syncCourtTypeTriggerLabel() {
    if (!courtTypeValue) return;
    courtTypeValue.textContent = getSelectedLabel("CourtType");
  }

  function syncSelectedValueLabel(name) {
    const target = selectedValueLabels[name];
    if (!target) {
      return;
    }

    const value = getSelectedLabel(name);
    target.textContent = value ? ` (${value})` : "";
    target.classList.toggle("hidden", !value);
  }

  function syncColorSectionLabels() {
    syncSelectedValueLabel("CourtColor");
    syncSelectedValueLabel("BorderColor");
    syncSelectedValueLabel("KeyColor");
    syncSelectedValueLabel("LineColor");
  }

  function syncSpecsButton() {
    if (!specsButton) return;
    const isOpen = shell.classList.contains("menu-open");
    specsButton.setAttribute("aria-expanded", isOpen ? "true" : "false");
    if (specsButtonLabel) {
      specsButtonLabel.textContent = isOpen ? "Close Specs" : "Open Specs";
    }
  }

  function openMenu() {
    shell.classList.add("menu-open");
    syncSpecsButton();
    if (!IS_EMBED) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        document.body.style.overflow = "hidden";
      }, 300);
    }
  }

  function closeMenu() {
    shell.classList.remove("menu-open");
    if (!IS_EMBED) {
      document.body.style.overflow = "";
    }
    syncSpecsButton();
  }

  function setCourtTypeMenuOpen(isOpen) {
    if (!courtTypeHolder || !courtTypeTrigger || !courtTypeMenu) {
      return;
    }

    courtTypeHolder.classList.toggle("open", isOpen);
    courtTypeTrigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
    courtTypeMenu.hidden = !isOpen;
  }

  function syncPreviewVisibility(isComplete) {
    if (welcomePanel) {
      welcomePanel.hidden = isComplete;
    }

    if (svgShell) {
      svgShell.hidden = !isComplete;
    }
  }

  function readFormState() {
    const courtType = getRadioValue("CourtType");
    const courtSize = getRadioValue("Sizes_" + courtType);

    return {
      courtType,
      courtSize,
      courtColor: getRadioValue("CourtColor"),
      borderColor: getRadioValue("BorderColor"),
      keyColor: getRadioValue("KeyColor"),
      lineColor: getRadioValue("LineColor"),
      accessories: new Set(
        ACCESSORIES.filter((accessory) => form.querySelector(`input[name="${accessory.field}"]:checked`)).map(
          (accessory) => accessory.value,
        ),
      ),
    };
  }

  function stopSectionAnimation(middleCol) {
    const animation = sectionAnimations.get(middleCol);
    if (animation) {
      animation.cancel();
      sectionAnimations.delete(middleCol);
    }
  }

  function collapseSection(middleCol) {
    if (!middleCol || middleCol.style.display === "none") return;
    stopSectionAnimation(middleCol);

    const h = middleCol.scrollHeight;
    middleCol.style.height = h + "px";
    middleCol.style.overflow = "hidden";
    middleCol.style.willChange = "height, opacity";

    const animation = animate(middleCol, { height: "0px", opacity: 0 }, { duration: 0.2, easing: [0.4, 0, 1, 1] });

    sectionAnimations.set(middleCol, animation);

    animation.finished
      .then(() => {
        middleCol.style.display = "none";
        middleCol.style.height = "";
        middleCol.style.overflow = "";
        middleCol.style.opacity = "";
        middleCol.style.willChange = "";
      })
      .catch(() => {})
      .finally(() => {
        if (sectionAnimations.get(middleCol) === animation) {
          sectionAnimations.delete(middleCol);
        }
      });
  }

  function expandSection(middleCol) {
    if (!middleCol) return;
    stopSectionAnimation(middleCol);

    middleCol.style.display = "";
    middleCol.style.overflow = "hidden";
    middleCol.style.height = "0px";
    middleCol.style.opacity = "0";
    middleCol.style.willChange = "height, opacity";
    const h = middleCol.scrollHeight;
    const animation = animate(
      middleCol,
      { height: h + "px", opacity: 1 },
      { duration: 0.22, easing: [0.22, 1, 0.36, 1] },
    );

    sectionAnimations.set(middleCol, animation);

    animation.finished
      .then(() => {
        middleCol.style.height = "";
        middleCol.style.overflow = "";
        middleCol.style.opacity = "";
        middleCol.style.willChange = "";
      })
      .catch(() => {})
      .finally(() => {
        if (sectionAnimations.get(middleCol) === animation) {
          sectionAnimations.delete(middleCol);
        }
      });
  }

  for (const t of COURT_TYPES) {
    if (sizeHolders[t]) sizeHolders[t].style.display = "none";
  }

  function updateSizeVisibility(courtType) {
    for (const t of COURT_TYPES) {
      if (sizeHolders[t]) {
        sizeHolders[t].style.display = t === courtType ? "" : "none";
      }
    }
  }

  function updateKeyColorVisibility(showKeyColor) {
    if (!keyColorHolder) return;
    keyColorHolder.style.display = showKeyColor ? "" : "none";
  }

  function syncAccessoryAvailability(allowedAccessories) {
    let didSanitizeSelection = false;

    for (const accessory of ACCESSORIES) {
      const input = form.querySelector(`input[name="${accessory.field}"]`);
      if (!input) continue;

      const option = input.closest("li");
      const isAllowed = allowedAccessories.has(accessory.value);

      input.disabled = !isAllowed;

      if (option) {
        option.hidden = !isAllowed;
      }

      if (!isAllowed && input.checked) {
        input.checked = false;
        didSanitizeSelection = true;
      }
    }

    return didSanitizeSelection;
  }

  function syncDataAttributes(state) {
    const { courtType, courtSize, courtColor, borderColor, keyColor, lineColor, accessories } = state;

    shell.setAttribute("data-court-type", courtType);
    shell.setAttribute("data-court-color", courtColor);
    shell.setAttribute("data-border-color", borderColor);
    shell.setAttribute("data-key-color", keyColor);
    shell.setAttribute("data-line-color", lineColor);

    for (const t of COURT_TYPES) {
      const attr = `data-${t.toLowerCase()}-size`;
      const val = t === courtType ? courtSize : "";
      if (val) {
        shell.setAttribute(attr, val);
      } else {
        shell.removeAttribute(attr);
      }
    }

    const courtHex = colorMap[courtColor];
    const borderHex = colorMap[borderColor];
    const keyHex = colorMap[keyColor];

    shell.style.setProperty("--court-color", courtHex ? `#${courtHex}` : "");
    shell.style.setProperty("--border-color", borderHex ? `#${borderHex}` : "");
    shell.style.setProperty("--key-color", keyHex ? `#${keyHex}` : "");
    shell.style.setProperty("--line-color", LINE_COLORS[lineColor] || "#fff");

    for (const a of ACCESSORIES) {
      shell.classList.toggle(a.cls, accessories.has(a.value));
    }

    return { courtType, courtSize };
  }

  function applyDerivedState() {
    const initialState = readFormState();
    const rules = getCourtRules(initialState.courtType, initialState.courtSize);

    updateSizeVisibility(initialState.courtType);
    updateKeyColorVisibility(rules.showKeyColor);

    const didSanitizeSelection = syncAccessoryAvailability(rules.allowedAccessories);
    const nextState = didSanitizeSelection ? readFormState() : initialState;

    syncDataAttributes(nextState);
    syncColorSectionLabels();

    return nextState;
  }

  async function loadSVG(courtType, size) {
    const combo = courtType + size;
    if (combo === currentCombo) return;
    currentCombo = combo;

    if (!courtType || !size || courtType === "Choose") {
      shell.classList.add("disabled");
      syncPreviewVisibility(false);
      if (svgShell) svgShell.innerHTML = "";
      return;
    }

    shell.classList.add("loading");
    shell.classList.remove("disabled");
    syncPreviewVisibility(true);

    try {
      const svgUrl = SVG_URL ? buildSVGRequestUrl(courtType, size) : `${SVG_BASE}${courtType}${size}.svg`;
      const resp = await fetch(svgUrl);
      if (!resp.ok) {
        throw new Error(`Failed to load court SVG: ${resp.status}`);
      }

      const text = await resp.text();
      if (!isLikelySVGResponse(text)) {
        throw new Error("Invalid SVG response received");
      }

      const href = window.location.href.replace(/#.*$/, "");
      const svg = text.replace(/"url\(#/g, `"url(${href}#`);
      if (svgShell) svgShell.innerHTML = svg;
    } catch (error) {
      if (svgShell) {
        svgShell.innerHTML = "";
      }
      console.error("[court-builder] svg-load", error);
    } finally {
      shell.classList.remove("loading");
    }
  }

  function buildSVGRequestUrl(courtType, size) {
    const url = new URL(SVG_URL);
    url.searchParams.set("court_type", courtType);
    url.searchParams.set("court_size", size);
    return url.toString();
  }

  function openFormSection(targetLabel) {
    if (!targetLabel) {
      targetLabel = [...sectionLabels].find((l) => l.closest(".field").style.display !== "none");
    }
    if (!targetLabel) return;

    for (const label of sectionLabels) {
      const mc = label.parentElement.querySelector(".middleColumn");
      if (label === targetLabel) {
        if (mc && mc.style.display === "none") {
          expandSection(mc);
        }
        label.parentElement.classList.add("clicked");
        label.setAttribute("aria-expanded", "true");
      } else {
        collapseSection(mc);
        label.setAttribute("aria-expanded", "false");
      }
    }
  }

  for (const label of sectionLabels) {
    const handler = () => {
      openFormSection(label);
    };
    label.addEventListener("click", handler, { signal });
    label.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handler();
        }
      },
      { signal },
    );
  }

  for (const label of sectionLabels) {
    const mc = label.parentElement.querySelector(".middleColumn");
    if (mc) {
      mc.style.display = "none";
    }
  }

  if (courtTypeHolder && courtTypeTrigger) {
    setCourtTypeMenuOpen(false);

    courtTypeTrigger.addEventListener(
      "click",
      (e) => {
        const isOpen = courtTypeHolder.classList.contains("open");
        if (!isOpen) {
          e.preventDefault();
        }
        setCourtTypeMenuOpen(!isOpen);
      },
      { signal },
    );

    courtTypeHolder.addEventListener(
      "change",
      () => {
        setCourtTypeMenuOpen(false);
        syncCourtTypeTriggerLabel();
        openMenu();
      },
      { signal },
    );
  }

  specsButton?.addEventListener(
    "click",
    () => {
      if (shell.classList.contains("menu-open")) {
        closeMenu();
        return;
      }

      openMenu();
    },
    { signal },
  );

  sidebarSubmitButton?.addEventListener("click", () => form.requestSubmit(), { signal });
  backdrop?.addEventListener("click", closeMenu, { signal });
  form.querySelector(".court-builder-sidebar-close")?.addEventListener("click", closeMenu, { signal });
  document.addEventListener(
    "click",
    (event) => {
      if (!courtTypeHolder || !courtTypeHolder.classList.contains("open")) return;
      if (courtTypeHolder.contains(event.target)) return;

      setCourtTypeMenuOpen(false);
    },
    { signal },
  );

  form.addEventListener(
    "change",
    () => {
      const nextCourtType = getRadioValue("CourtType");
      if (currentCourtType && nextCourtType !== currentCourtType) {
        resetDependentFields(nextCourtType);
      }

      const nextState = applyDerivedState();
      const { courtType: ct, courtSize } = nextState;
      const combo = ct + courtSize;
      if (combo !== currentCombo) {
        if (ct !== currentCourtType && ct && ct !== "Choose") {
          openFormSection();
        }
        currentCourtType = ct;
        loadSVG(ct, courtSize);
      }
    },
    { signal },
  );

  form.addEventListener(
    "submit",
    (e) => {
      e.preventDefault();

      const state = applyDerivedState();
      const { courtType, courtSize, courtColor, borderColor, keyColor, lineColor, accessories } = state;
      const rules = getCourtRules(courtType, courtSize);

      const acc = [...accessories]
        .filter((accessory) => rules.allowedAccessories.has(accessory))
        .map((accessory) => accessory.replace(/ /g, "_"))
        .join(",");

      const targetViewUrl = shell.dataset.courtBuilderViewUrl;
      const url = targetViewUrl ? new URL(targetViewUrl) : new URL("view", window.location.href);
      url.searchParams.set("court_type", courtType);
      url.searchParams.set("court_size", courtSize);
      url.searchParams.set("court_color", courtColor.replace(/ /g, "_"));
      url.searchParams.set("border_color", borderColor.replace(/ /g, "_"));

      if (courtType !== "Tennis" && courtType !== "Pickleball") {
        url.searchParams.set("key_color", keyColor.replace(/ /g, "_"));
      }

      url.searchParams.set("line_color", lineColor);

      if (acc) url.searchParams.set("accessories", acc);

      if (IS_EMBED && typeof window._mfhcembedNavigate === "function") {
        window._mfhcembedNavigate(url.toString());
        return;
      }

      window.location.href = url.toString();
    },
    { signal },
  );

  syncCourtTypeTriggerLabel();
  syncSpecsButton();
  form.dispatchEvent(new Event("change"));

  shell._courtBuilderCleanup = () => {
    destroyTooltips(colorTooltipInstances);
    abortController.abort();
  };

  async function initColorTooltips() {
    const targets = form.querySelectorAll(
      [
        `#${FORM_ID}_CourtColor_Holder [data-court-builder-color-option]`,
        `#${FORM_ID}_BorderColor_Holder [data-court-builder-color-option]`,
        `#${FORM_ID}_KeyColor_Holder [data-court-builder-color-option]`,
        `#${FORM_ID}_LineColor_Holder [data-court-builder-color-option]`,
      ].join(", "),
    );

    if (!targets.length) {
      return [];
    }

    const { default: tippy } = await import("tippy.js");
    await import("tippy.js/dist/tippy.css");

    return toTooltipArray(
      tippy(targets, {
        content(reference) {
          return reference.dataset.courtBuilderColorOption || "";
        },
        placement: "top",
      }),
    );
  }
}

function withTrailingSlash(value) {
  return value.endsWith("/") ? value : `${value}/`;
}

function destroyTooltips(instances) {
  for (const instance of instances) {
    instance.destroy();
  }
}

function toTooltipArray(instances) {
  if (!instances) {
    return [];
  }

  return Array.isArray(instances) ? instances : [instances];
}

function isLikelySVGResponse(text) {
  const trimmed = text.trim();
  const svgIndex = trimmed.search(/<svg\b/i);

  if (svgIndex === -1) {
    return false;
  }

  const htmlIndex = trimmed.search(/<html\b/i);

  return htmlIndex === -1 || svgIndex < htmlIndex;
}

if (document.getElementById("mfcbroot")) {
  initCourtBuilder(document);
}
